import { Box, Button, Popper, Tooltip, Typography } from '@mui/material';
import { ReactNode, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { sharedColors } from '../../utils/Style';
import { useThemeColor } from '../../hooks/useThemeColor';
import { calculateDiscountRate } from '../../utils/Pricing';
import { isTokenValid } from '../../utils/Auth';
import { setStockAlert } from '../../api/StockAlert';
import { AddToBasketPopper } from './AddToBasketPopper';

type OverlayChipProps = {
  text: string;
  color: string;
};

const OverlayChip = ({ text, color }: OverlayChipProps) => {
  const { secondary } = useThemeColor();

  return (
    <Box sx={{ backgroundColor: color, p: '3px 5px 2px', borderRadius: '3px' }}>
      <Typography sx={{ color: secondary, fontSize: '11px', fontWeight: 500 }}>
        {text}
      </Typography>
    </Box>
  );
};

type ProductCardProps = {
  productSummary: ProductSummary;
};

export const ProductCard = ({ productSummary }: ProductCardProps) => {
  const navigate = useNavigate();

  const { primary, secondary, ternary } = useThemeColor();

  const [hovering, setHovering] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSetStockAlert = useCallback(() => {
    if (isTokenValid()) {
      setStockAlert({
        productID: productSummary.id,
      })
        .then(() => {
          toast.success(
            'Ürün stokları yenilendiğinde E-Posta ile haberdar edileceksiniz',
          );
        })
        .catch((error) => toast.error(error.message));
    } else {
      navigate('/giris-yap');
    }
  }, [productSummary]);

  const chips: ReactNode[] = [];
  if (productSummary.specialPrice) {
    chips.push(
      <OverlayChip
        text={`%${calculateDiscountRate(
          productSummary.price,
          productSummary.specialPrice,
        ).toString()}`}
        color={primary}
      />,
    );
  }
  if (!productSummary.inStock) {
    chips.push(<OverlayChip text='YAKINDA' color={ternary} />);
  } else if (productSummary.quantity < 6) {
    chips.push(<OverlayChip text='TÜKENMEK ÜZERE' color={ternary} />);
  }

  return (
    <Link
      to={`/urun/${productSummary.slug}`}
      style={{ textDecoration: 'none' }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
        }}
      >
        <Box
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          sx={{
            position: 'relative',
            width: '100%',
            paddingBottom: '120%', // 1.2 * width = 120% height
            overflow: 'hidden',
          }}
        >
          <Box
            component='img'
            src={
              hovering && !!productSummary.secondImage
                ? productSummary.secondImage
                : productSummary.imagePath
            }
            width='100%'
            height='100%'
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              objectFit: 'contain',
            }}
          />

          {chips.map((chip, i) => (
            <Box
              key={i}
              position='absolute'
              top={5 + Math.floor(i / 2) * 30}
              left={i % 2 === 0 ? 5 : undefined}
              right={i % 2 === 0 ? undefined : 5}
            >
              {chip}
            </Box>
          ))}
        </Box>

        <Tooltip title={productSummary.name}>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 600,
              textAlign: 'center',
              color: sharedColors.gray7,
              transition: 'color 0.3s ease',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              lineHeight: '1.3em',
              maxHeight: '2.6em',
              minHeight: '2.6em',
              '&:hover': {
                color: primary,
              },
            }}
          >
            {productSummary.name}
          </Typography>
        </Tooltip>

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
          {!!productSummary.specialPrice && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '15px',
                color: sharedColors.gray5,
                textDecoration: 'line-through',
              }}
            >
              {productSummary.priceFormatted}
            </Typography>
          )}

          <Typography
            sx={{ fontWeight: 600, fontSize: '15px', color: primary }}
          >
            {productSummary.specialPrice
              ? productSummary.specialPriceFormatted
              : productSummary.priceFormatted}
          </Typography>
        </Box>

        <Button
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();

            if (productSummary.inStock) {
              setAnchorEl(ev.currentTarget);
            } else {
              handleSetStockAlert();
            }
          }}
          sx={{
            fontWeight: 700,
            fontSize: '13px',
            padding: '8px 15px',
            color: secondary,
            backgroundColor: productSummary.inStock ? primary : ternary,
            transition: 'background-color 0.3s ease',
            '&:hover': {
              backgroundColor: ternary,
            },
          }}
        >
          {productSummary.inStock ? 'Sepete Ekle' : 'Haber Ver'}
        </Button>

        <Popper open={!!anchorEl} anchorEl={anchorEl}>
          <AddToBasketPopper
            productSlug={productSummary.slug}
            onClose={() => setAnchorEl(null)}
          />
        </Popper>
      </Box>
    </Link>
  );
};
